import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth, UserManager } from 'oidc-react';
import Navbar from './components/Layout/Navbar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Upload from './pages/Upload';
import ProtectedBricks from './pages/ProtectedBricks';
import Account from './pages/Account';
import Documents from './pages/Documents';
import Unauthorized from './pages/Unauthorized';
import { hasRequiredAccess } from './utils/roleHelper';
import { apiService } from './services/apiService';

// Token State Typen
type TokenState = 'idle' | 'checking' | 'valid' | 'invalid';

// Context für Token-Status
interface TokenContextType {
    tokenState: TokenState;
    isTokenValid: boolean;
}

const TokenContext = createContext<TokenContextType>({
    tokenState: 'idle',
    isTokenValid: false,
});

// Hook zum Abrufen des Token-Status
export const useTokenState = () => useContext(TokenContext);


// Create Material-UI theme
const theme = createTheme({
    palette: {
        primary: {
            main: '#2563eb',
            light: '#60a5fa',
            dark: '#1e40af',
        },
        secondary: {
            main: '#64748b',
        },
        success: {
            main: '#22c55e',
            light: '#86efac',
            dark: '#15803d',
        },
        error: {
            main: '#ef4444',
            light: '#fca5a5',
            dark: '#b91c1c',
        },
        warning: {
            main: '#f59e0b',
            light: '#fcd34d',
            dark: '#d97706',
        },
        background: {
            default: '#f8fafc',
            paper: '#ffffff',
        },
    },
    typography: {
        fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
        ].join(','),
    },
    shape: {
        borderRadius: 8,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                },
            },
        },
    },
});

// Ladebildschirm-Komponente
const LoadingScreen: React.FC<{ message?: string }> = ({ message = 'Lädt...' }) => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
    }}>
        {message}
    </div>
);

// Protected Route Component
// Prüft ob User eingeloggt ist UND ob der Token gültig ist
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const auth = useAuth();
    const { tokenState, isTokenValid } = useTokenState();

    // Warte bis OIDC und Token-Validierung abgeschlossen sind
    if (auth.isLoading || tokenState === 'idle' || tokenState === 'checking') {
        return <LoadingScreen message="Authentifizierung wird geprüft..." />;
    }

    // Kein User eingeloggt
    if (!auth.userData) {
        auth.signIn();
        return null;
    }

    // Token ist nicht gültig (abgelaufen oder Refresh fehlgeschlagen)
    if (!isTokenValid) {
        console.log('ProtectedRoute: Token ungültig, leite zum Login weiter');
        auth.signIn();
        return null;
    }

    return <>{children}</>;
};

// Role-Based Route Component
// Prüft Rollen und Gruppen aus ID Token UND Access Token
// Prüft zusätzlich ob der Token gültig ist
const RoleBasedRoute: React.FC<{
    children: React.ReactNode;
    allowedGroups?: string[];
    allowedRoles?: string[];
}> = ({ children, allowedGroups = [], allowedRoles = [] }) => {
    const auth = useAuth();
    const { tokenState, isTokenValid } = useTokenState();

    // Warte bis OIDC und Token-Validierung abgeschlossen sind
    if (auth.isLoading || tokenState === 'idle' || tokenState === 'checking') {
        return <LoadingScreen message="Authentifizierung wird geprüft..." />;
    }

    // Kein User eingeloggt
    if (!auth.userData) {
        auth.signIn();
        return null;
    }

    // Token ist nicht gültig (abgelaufen oder Refresh fehlgeschlagen)
    if (!isTokenValid) {
        console.log('RoleBasedRoute: Token ungültig, leite zum Login weiter');
        auth.signIn();
        return null;
    }

    // Prüfe Zugriff basierend auf Gruppen und Rollen aus beiden Tokens
    if (!hasRequiredAccess(auth.userData, allowedGroups, allowedRoles)) {
        return <Navigate to="/unauthorized" replace />;
    }

    return <>{children}</>;
};

// Helper: Prüft ob Token abgelaufen ist
const isTokenExpired = (expiresAt: number | undefined): boolean => {
    if (!expiresAt) return false;
    const now = Math.floor(Date.now() / 1000);
    return expiresAt <= now;
};

// App Routes Component
const AppRoutes: React.FC = () => {
    const auth = useAuth();
    // 'idle' = initial, 'checking' = wird geprüft, 'valid' = Token OK, 'invalid' = kein/ungültiger Token
    const [tokenState, setTokenState] = useState<TokenState>('idle');

    // Prüfe Token und refresh wenn nötig, setze TokenProvider BEVOR tokenState auf 'valid' gesetzt wird
    useEffect(() => {
        let isMounted = true;

        const checkAndRefreshToken = async () => {
            // Warte bis OIDC geladen hat
            if (auth.isLoading) {
                return;
            }

            // Kein User eingeloggt
            if (!auth.userData) {
                apiService.setTokenProvider(null);
                if (isMounted) setTokenState('invalid');
                return;
            }

            // Prüfe ob Token abgelaufen
            if (isTokenExpired(auth.userData.expires_at)) {
                console.log('Token abgelaufen, starte Silent Renew...');
                if (isMounted) setTokenState('checking');

                try {
                    await auth.userManager?.signinSilent();
                    console.log('Silent Renew erfolgreich');
                    // Nach signinSilent wird auth.userData aktualisiert,
                    // was diesen useEffect erneut triggert
                } catch (error) {
                    console.error('Silent Renew fehlgeschlagen, leite zum Login weiter:', error);
                    if (isMounted) {
                        apiService.setTokenProvider(null);
                        setTokenState('invalid');
                        auth.signIn();
                    }
                }
            } else {
                // Token ist gültig - ERST TokenProvider setzen, DANN State aktualisieren
                const accessToken = auth.userData.access_token;
                if (accessToken) {
                    console.log('Token-Provider gesetzt');
                    apiService.setTokenProvider(() => accessToken);
                }
                if (isMounted) setTokenState('valid');
            }
        };

        checkAndRefreshToken();

        return () => {
            isMounted = false;
        };
    }, [auth.isLoading, auth.userData, auth.userManager, auth]);

    // Token-Context-Wert
    const tokenContextValue: TokenContextType = {
        tokenState,
        isTokenValid: tokenState === 'valid',
    };

    return (
        <TokenContext.Provider value={tokenContextValue}>
            {auth.userData && <Navbar />}
            <Routes>
                <Route
                    path="/"
                    element={auth.userData ? <Navigate to="/dashboard" replace /> : <Login />}
                />
                <Route
                    path="/dashboard"
                    element={
                        <ProtectedRoute>
                            <Dashboard />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/upload"
                    element={
                        <ProtectedRoute>
                            <Upload />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/protected-bricks"
                    element={
                        <RoleBasedRoute
                            allowedGroups={['website-manager']}
                            allowedRoles={['frontend-manager']}
                        >
                            <ProtectedBricks />
                        </RoleBasedRoute>
                    }
                />
                <Route
                    path="/account"
                    element={
                        <ProtectedRoute>
                            <Account />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/documents"
                    element={
                        <ProtectedRoute>
                            <Documents />
                        </ProtectedRoute>
                    }
                />
                <Route path="/unauthorized" element={<Unauthorized />} />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
        </TokenContext.Provider>
    );
};

// Main App Component
const App: React.FC = () => {
    const [userManager] = useState(() => new UserManager({
        authority: process.env.REACT_APP_AUTHORITY as string,
        client_id: process.env.REACT_APP_CLIENT_ID || 'pb-frontend',
        redirect_uri: process.env.REACT_APP_REDIRECT_URI || window.location.origin,
        response_type: 'code',
        scope: process.env.REACT_APP_SCOPE || 'openid profile email',
        automaticSilentRenew: true,
        silent_redirect_uri: process.env.REACT_APP_SILENT_REDIRECT_URI || window.location.origin,
        post_logout_redirect_uri: process.env.REACT_APP_POST_LOGOUT_REDIRECT_URI || window.location.origin,
        // loadUserInfo: true,
    }));

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
                <AuthProvider
                    userManager={userManager}
                    autoSignIn={false}                    
                    // onSignIn={() => {
                    //     window.history.replaceState({}, document.title, window.location.pathname);
                    // }}
                >
                    <AppRoutes />
                </AuthProvider>
            </Router>
        </ThemeProvider>
    );
};

export default App;
